import React, { useState } from 'react';
import Text from 'antd/lib/typography/Text';
import VerifyEmailStep from './VerifyEmailStep';
import VerifyPasswordStep from './VerifyPasswordStep';
import ResetPasswordStep from './ResetPasswordStep';
import VerifyPhoneNumberStep from './VerifyPhoneNumberStep';
import VerificationCodeStep from './VerificationCodeStep';
import { useIdTokenResultQuery } from '../../../hooks/Auth';
import moment from 'moment';
import EnrollPhoneNumberStep from './EnrollPhoneNumberStep';
import { useProfileQuery } from '../../../hooks/Profile';
import { firstMultiFactor } from '../../../utils/ProfileUtils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../auth/firebaseAuth';

enum Step {
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  PASSWORD = 'PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD',
  PHONE_NUMBER = 'PHONE_NUMBER',
  VERIFICATION_CODE = 'VERIFICATION_CODE',
}

interface VerifyMultiFactorFlowProps {
  email: string;
  initialStep: Step;
  hasPhoneNumber: boolean;
  onComplete: () => void;
  onCancel?: () => void;
}

const VerifyMultiFactorFlow: React.FC<VerifyMultiFactorFlowProps> = ({
  email,
  initialStep,
  hasPhoneNumber,
  onComplete,
  onCancel,
}) => {
  const [step, setStep] = useState<Step>(initialStep);
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [verificationId, setVerificationId] = useState<string>();
  const onVerificationCodeSent = (phoneNumber: string, verificationId: string) => {
    setPhoneNumber(phoneNumber);
    setVerificationId(verificationId);
    setStep(Step.VERIFICATION_CODE);
  };
  switch (step) {
    case Step.VERIFY_EMAIL:
      return (
        <VerifyEmailStep
          subtitle={
            <div className="text-content-secondary">
              For your account security, verify your email to continue. We will send a secure link to{' '}
              <Text strong ellipsis>
                {email}
              </Text>
              .
            </div>
          }
          onCancel={onCancel}
        />
      );
    case Step.PASSWORD:
      return (
        <VerifyPasswordStep
          onNext={() => setStep(Step.PHONE_NUMBER)}
          onCancel={onCancel}
          onForgotPassword={() => setStep(Step.RESET_PASSWORD)}
        />
      );
    case Step.RESET_PASSWORD:
      return <ResetPasswordStep onCancel={() => setStep(Step.PASSWORD)} />;
    case Step.PHONE_NUMBER:
      if (!hasPhoneNumber) {
        return <EnrollPhoneNumberStep onVerificationCodeSent={onVerificationCodeSent} onCancel={onCancel} />;
      }
      return <VerifyPhoneNumberStep onVerificationCodeSent={onVerificationCodeSent} onCancel={onCancel} />;
    case Step.VERIFICATION_CODE:
      if (!phoneNumber || !verificationId) {
        setStep(Step.PHONE_NUMBER);
        return <></>;
      }
      return (
        <VerificationCodeStep
          phoneNumber={phoneNumber}
          verificationId={verificationId}
          onComplete={onComplete}
          onCancel={() => setStep(Step.PHONE_NUMBER)}
        />
      );
  }
};

type VerifyMultiFactorFlowWrapperProps = Omit<VerifyMultiFactorFlowProps, 'email' | 'initialStep' | 'hasPhoneNumber'>;

const VerifyMultiFactorFlowWrapper: React.FC<VerifyMultiFactorFlowWrapperProps> = (props) => {
  const [user] = useAuthState(auth);
  const idTokenResult = useIdTokenResultQuery().data;
  const profile = useProfileQuery().data;
  const multiFactor = firstMultiFactor(profile);
  if (!user || !idTokenResult) {
    return <></>;
  }
  const { email, emailVerified } = user;
  if (!email) {
    return <></>;
  }
  const recentLogin = moment(idTokenResult.authTime).isAfter(moment().subtract(15, 'minutes'));
  const hasPhoneNumber = !!multiFactor;
  return (
    <VerifyMultiFactorFlow
      {...props}
      email={email}
      hasPhoneNumber={hasPhoneNumber}
      initialStep={initialStep(emailVerified, recentLogin)}
    />
  );
};

const initialStep = (emailVerified: boolean, recentLogin: boolean) => {
  if (!emailVerified) {
    return Step.VERIFY_EMAIL;
  }
  // TODO(zhixuan): Require recent users to reauthenticate before enrolling/verifying 2FA if they authenticated a long time ago.
  return Step.PHONE_NUMBER; // recentLogin ? Step.PHONE_NUMBER : Step.PASSWORD;
};

export default VerifyMultiFactorFlowWrapper;
